import { useState, useCallback } from "react";
import JSZip from "jszip";
import { parseHachsharaZip } from "../parsers/hachsharaParser";

// Loads Hachshara ZIP files, parses them, and matches members to existing clients.
// Returns a preview result for the user to confirm before saving anything.
export function useHachsharaBatchLoader() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // existingClients: the unified client list from App (advice + holdings merged by ת"ז)
  const loadZips = useCallback(async (files, existingClients) => {
    setBusy(true);
    setError(null);

    const results = []; // one entry per ZIP

    for (const file of Array.from(files)) {
      if (!file.name.toLowerCase().endsWith('.zip')) continue;
      try {
        const zip = await JSZip.loadAsync(file);

        // Collect all entries as ArrayBuffer for byte-level parsing
        const zipFiles = await Promise.all(
          Object.values(zip.files)
            .filter(e => !e.dir)
            .map(async e => ({
              name: e.name.replace(/.*\//, ''), // strip folder prefix if any
              content: await e.async('arraybuffer'),
            }))
        );

        const parsed = parseHachsharaZip(file.name, zipFiles);

        // Match each member to an existing client by ת"ז
        const matched = [];
        const unmatched = [];

        for (const member of parsed.members) {
          const existing = existingClients.find(c => c.clientId === member.taz);
          if (existing) {
            matched.push({ ...member, existingClientId: existing.id, existingName: existing.clientName });
          } else {
            const autoName = [member.firstName, member.lastName].filter(Boolean).join(' ');
            unmatched.push({ ...member, newName: autoName });
          }
        }

        results.push({
          fileName: file.name,
          companyCode: parsed.companyCode,
          reportDate: parsed.reportDate,
          matched,
          unmatched,
          totalBalance: parsed.members.reduce(
            (sum, m) => sum + m.funds.reduce((s, f) => s + f.balance, 0), 0
          ),
        });
      } catch (err) {
        results.push({ fileName: file.name, error: err.message });
      }
    }

    setBusy(false);
    return results;
  }, []);

  return { loadZips, busy, error };
}
